import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../main/AuthShell';
import Icon from '../../components/common/Icon';
import BravosQuestions from './BravosQuestions';
import { apiRequest } from '../../utils';
import './MemberHome.css';
import './BravosSurveyFlow.css';

/* Public distributed assessment (no account) — reached from a share link a member sends to a
   leader/employee. The token in the URL carries which instrument + company it belongs to; the
   submission is always anonymous, tied to that company. Backed by GET /api/bravos/assess/:token
   and POST /api/bravos/assess/:token/submit.

   This is the OFFICIAL, scored assessment (cxo_assessment / employee_assessment), so — unlike the
   public lead-magnet pulses — there are NO interstitials, no "working" animation and no email
   opt-in: just paginated questions and a submit. Paginated 3 questions per page with Back / Next
   and a progress bar; every scale/choice question on a page must be answered before moving on. All
   answers are POSTed together as one JSON blob keyed by question id (what the scoring engine reads). */

const PER_PAGE = 3;
// scale + choice questions must be answered before leaving a page; free text stays optional.
const isRequired = (q) => !!q && (q.type === 'choice' || q.type === 'scale');
const isAnswered = (v) => v !== undefined && v !== null && v !== '';

const BravosAssess = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    apiRequest('GET', `/bravos/assess/${token}`)
      .then(setMeta)
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const questions = useMemo(() => (meta && meta.questions) || [], [meta]);
  const pages = useMemo(() => {
    const out = [];
    for (let k = 0; k < questions.length; k += PER_PAGE) out.push(questions.slice(k, k + PER_PAGE));
    return out;
  }, [questions]);

  const pageQuestions = pages[page] || [];
  const isLastPage = page >= pages.length - 1;
  // Every required question on THIS page must be answered before Next / Submit.
  const pageComplete = pageQuestions.every((q) => !isRequired(q) || isAnswered(answers[q.id]));

  const from = page * PER_PAGE + 1;
  const to = Math.min(from + pageQuestions.length - 1, questions.length);
  const pct = questions.length ? Math.round((to / questions.length) * 100) : 0;

  const back = () => { if (page > 0) { setPage(page - 1); window.scrollTo(0, 0); } };
  const next = () => {
    if (!pageComplete || isLastPage) return;
    setPage(page + 1);
    window.scrollTo(0, 0);
  };

  const submit = async () => {
    if (!pageComplete) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiRequest('POST', `/bravos/assess/${token}/submit`, { answers });
      setDone(true);
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={meta ? meta.name : 'Bravos'}>
      <div className="auth__card">
        {loading ? (
          <span className="auth__spinner" />
        ) : invalid || !meta ? (
          <p>{t('invalidLink')}</p>
        ) : done ? (
          <div className="bravos-recorded">
            <Icon name="check" /> {t('assessmentRecorded')}
          </div>
        ) : !questions.length ? (
          <p className="bravos-q__hint">{t('comingSoon')}</p>
        ) : (
          <div className="bravos-flow">
            {/* Anonymity note only on the first page, so later pages stay compact on mobile. */}
            {page === 0 && (
              <p className="bravos-q__hint bravos-flow__header">{t('assessmentAnonymousNote')}</p>
            )}

            <div className="bravos-flow__bar">
              <div className="bravos-flow__fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="bravos-flow__count">
              {t('assessmentProgress', { from, to, total: questions.length })}
            </p>

            <BravosQuestions questions={pageQuestions} answers={answers} setAnswer={setAnswer} />

            {error && <p className="text-danger">{error}</p>}

            <div className="bravos-flow__nav">
              {page > 0 && (
                <button type="button" className="bravos-submit bravos-submit--ghost" onClick={back}>
                  <Icon name="arrow-left" /> {t('back')}
                </button>
              )}
              {isLastPage ? (
                <button
                  type="button" className="bravos-submit" onClick={submit}
                  disabled={submitting || !pageComplete}
                >
                  {submitting ? <span className="auth__spinner" /> : <Icon name="check" />}
                  {t('submit')}
                </button>
              ) : (
                <button
                  type="button" className="bravos-submit" onClick={next} disabled={!pageComplete}
                >
                  {t('surveyNext')} <Icon name="arrow-right" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </AuthShell>
  );
};

export default BravosAssess;
